import { useEffect, useState, type ReactNode } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { cn } from "../../lib/cn";

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "destructive";
  pending?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  pending = false,
}: ConfirmDialogProps) {
  if (!open) return null;
  return (
    <Modal
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === "destructive" ? "danger" : "primary"}
            onClick={() => void onConfirm()}
            disabled={pending}
          >
            {pending ? "Working…" : confirmLabel}
          </Button>
        </>
      }
    >
      <div />
    </Modal>
  );
}

interface TypedConfirmDialogProps extends Omit<ConfirmDialogProps, "onConfirm"> {
  requireText: string;
  onConfirm: () => void | Promise<void>;
}

/**
 * Permanent-delete confirmation: the user must type the exact `requireText`
 * (case-sensitive) before the confirm button activates. Used for project
 * hard-delete.
 */
export function TypedConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  requireText,
  confirmLabel = "Delete permanently",
  cancelLabel = "Cancel",
  pending = false,
}: TypedConfirmDialogProps) {
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  const matches = typed === requireText;
  if (!open) return null;

  return (
    <Modal
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button
            variant="danger"
            onClick={() => void onConfirm()}
            disabled={!matches || pending}
          >
            {pending ? "Working…" : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="text-small text-text-secondary">
          Type{" "}
          <span className="text-mono text-text-primary">{requireText}</span>{" "}
          to confirm. This cannot be undone.
        </div>
        <input
          autoFocus
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={requireText}
          className={cn(
            "h-10 px-3 bg-bg-inset rounded-md",
            "border transition-colors duration-fast ease-out",
            matches
              ? "border-status-error"
              : "border-transparent hover:border-border-default focus:border-border-strong",
            "text-body text-text-primary placeholder:text-text-quaternary outline-none",
          )}
        />
      </div>
    </Modal>
  );
}
